// WHICH RISK A TICKET PRINTS, and the one function allowed to decide it.
//
// A tier is a claim about a ticket's CHANCE — "Low risk" says this is likelier
// to land than anything else on the pad — and for most of this app's life
// Classic wrote that claim by hand: the two-leg ticket was called `low` because
// it was the two-leg ticket, and the racing all-up called its two-leg ticket
// `low` for the same reason. So a pad could print "Low risk · 1 in 19 chance"
// on one line, which is the label arguing with the number directly beneath it,
// and the reader has no way of telling which of the two to believe.
//
// The fix is a rule, not a better set of hand-written labels: the four words
// are BANDS ON THE COMBINED PROBABILITY, and nothing anywhere in the app may
// write a tier string except `tierFor`. What a strategy chooses is its own
// business — leg counts, prices, designs — and how the result is labelled is
// this file's. `strategies/*.js` say what to pick; this says what it is.
//
// THE LEG COUNT IS STILL THE PRODUCT. Classic's four ways are four leg-count
// DESIGNS (2, 3, 4–5, 6–8) and the racing all-up's three are 2, 3 and 4; that
// is what a punter is choosing between, it is printed on every ticket as the
// leg count, and it is what the caps and the ids are keyed by. The colour on
// top of it is the honest reading of the chance, which is sometimes not the one
// the design would have guessed.
//
// A LEAF, and it has to be: `tickets.js` prints Classic's tickets and
// `engine/strategy.js` prints everything else, and strategy.js already imports
// tickets.js. Either of them owning this would be a cycle.

/** The four, strongest first. Also the order Today prints a Classic pad in. */
export const TIER_ORDER = ['low', 'medium', 'high', 'outrageous'];

/**
 * The bands, as [min, max) on combined probability.
 *
 * Moving a number here recolours every ticket in the app and changes what none
 * of them contain. 45% is "better than a coin flip on the day"; 5% is where a
 * multi stops being a bet with a plan and becomes a lottery ticket, which is
 * what the outrageous ink has always been for.
 */
export const TIER_BANDS = {
  low: [0.45, Infinity],
  medium: [0.18, 0.45],
  high: [0.05, 0.18],
  outrageous: [0, 0.05],
};

/**
 * The tier a combined probability earns, or NULL where there is no probability
 * to read.
 *
 * Null rather than "outrageous" for a ticket with no chance on it — a void
 * design has no legs and therefore no chance, and calling that outrageous
 * would be the pad inventing the one number it does not have. Callers print the
 * design's own name in that case (`DESIGN_LABEL` in tickets.js).
 */
export function tierFor(p) {
  if (!Number.isFinite(p)) return null;
  return TIER_ORDER.find((t) => p >= TIER_BANDS[t][0] && p < TIER_BANDS[t][1]) ?? 'outrageous';
}
